import { useEffect, useRef, useState } from "react";
import { LockKey, Backspace, ShieldCheck } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

const PinDialog = ({
  open,
  onOpenChange,
  onSubmit,
  pinLength = 4,
  submitting = false,
  errorMsg = null,
}) => {
  const [pin, setPin] = useState("");
  const [shake, setShake] = useState(false);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (open) {
      setPin("");
      submittedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (errorMsg) {
      setShake(true);
      const t = setTimeout(() => {
        setShake(false);
        setPin("");
        submittedRef.current = false;
      }, 450);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  // Auto-submit when full PIN typed
  useEffect(() => {
    if (
      open &&
      pin.length === pinLength &&
      !submittedRef.current &&
      !submitting
    ) {
      submittedRef.current = true;
      onSubmit(pin);
    }
  }, [pin, pinLength, onSubmit, submitting, open]);

  // Keyboard support
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key >= "0" && e.key <= "9") {
        setPin((p) => (p.length < pinLength ? p + e.key : p));
      } else if (e.key === "Backspace") {
        setPin((p) => p.slice(0, -1));
      } else if (e.key === "Escape") {
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, pinLength, onOpenChange]);

  const handleKey = (k) => {
    if (submitting) return;
    if (k === "back") {
      setPin((p) => p.slice(0, -1));
    } else if (k && pin.length < pinLength) {
      setPin((p) => p + k);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface border border-border max-w-sm text-white"
        data-testid="pin-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading font-black tracking-tight text-2xl flex items-center gap-2">
            <LockKey size={22} weight="duotone" className="text-aqua" />
            Introduce tu PIN
          </DialogTitle>
          <DialogDescription className="text-white/60">
            Ingresa el PIN de {pinLength} dígitos para confirmar tu check-in.
          </DialogDescription>
        </DialogHeader>

        {/* PIN dots */}
        <div
          className={
            "flex items-center justify-center gap-3 my-6 transition-transform " +
            (shake ? "animate-[wiggle_0.45s_ease-in-out]" : "")
          }
          style={
            shake
              ? {
                  animation: "pinShake 0.45s ease-in-out",
                }
              : undefined
          }
          data-testid="pin-dots"
        >
          {Array.from({ length: pinLength }).map((_, i) => {
            const filled = i < pin.length;
            const isError = !!errorMsg;
            return (
              <div
                key={i}
                className={
                  "w-4 h-4 rounded-full border-2 transition-all duration-150 " +
                  (isError
                    ? "border-danger bg-danger"
                    : filled
                    ? "border-aqua bg-aqua shadow-[0_0_12px_rgba(0,229,255,0.7)]"
                    : "border-white/25 bg-transparent")
                }
              />
            );
          })}
        </div>

        {errorMsg && (
          <div
            className="text-center text-sm text-danger -mt-2 mb-2 font-medium"
            data-testid="pin-error"
          >
            {errorMsg}
          </div>
        )}

        {/* Numeric keypad */}
        <div className="grid grid-cols-3 gap-2 max-w-[260px] mx-auto w-full">
          {KEYS.map((k, i) => {
            if (k === "") return <div key={i} />;
            if (k === "back") {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleKey("back")}
                  disabled={submitting || pin.length === 0}
                  className="aspect-square rounded-xl bg-background border border-border hover:border-aqua/50 hover:text-aqua transition-colors flex items-center justify-center text-white/80 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="pin-key-back"
                >
                  <Backspace size={22} weight="duotone" />
                </button>
              );
            }
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleKey(k)}
                disabled={submitting || pin.length >= pinLength}
                className="aspect-square rounded-xl bg-background border border-border hover:border-aqua/50 hover:bg-aqua/5 transition-colors flex items-center justify-center text-2xl mono font-mono font-medium text-white disabled:opacity-40"
                data-testid={`pin-key-${k}`}
              >
                {k}
              </button>
            );
          })}
        </div>

        <div className="mt-5 text-center text-xs text-white/40 inline-flex items-center justify-center gap-1.5">
          <ShieldCheck size={12} weight="duotone" />
          El PIN evita activaciones accidentales del check-in.
        </div>

        <style>{`
          @keyframes pinShake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default PinDialog;
